import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { themedFonts, useCurrentTheme } from '@gitbook/react-uikit';
import { Description, FieldGroup, Group, Label } from '@gitbook/spine-react';

import { ContentKitSelect } from './ContentKitSelect';
import { ContentKitTextStyleContext } from './ContentKitText';
import type { ContentKitElementProps, ContentKitElementRef } from './types';

export function ContentKitInput(
    props: ContentKitElementProps<api.ContentKitInput>
): React.ReactElement {
    const { element, renderElement } = props;

    const ref = React.useRef<ContentKitElementRef>(null);

    const [, font] = useCurrentTheme(themedFonts);

    const label = <Label>{element.label}</Label>;
    const hint = element.hint ? (
        <ContentKitTextStyleContext.Provider value={[font.Label, font.textMid]}>
            <Description>
                {typeof element.hint === 'string' ? element.hint : renderElement(element.hint)}
            </Description>
        </ContentKitTextStyleContext.Provider>
    ) : null;

    const onPress = () => {
        if (ref.current) {
            ref.current.press?.();
        }
    };

    const onLabelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onPress();
        }
    };

    // Vertical
    if (['textinput', 'codeblock'].includes(element.element.type)) {
        return (
            <div>
                <div className="mb-1">{label}</div>
                {hint ? <div className="mb-2">{hint}</div> : null}
                {renderElement(element.element)}
            </div>
        );
    }

    if (['radio', 'checkbox'].includes(element.element.type)) {
        // Horizontal, input on the left
        return (
            <div className="flex items-center">
                <div className="pr-4">{renderElement(element.element, ref)}</div>
                <div
                    role="button"
                    tabIndex={0}
                    className="flex-1 cursor-pointer"
                    onClick={onPress}
                    onKeyDown={onLabelKeyDown}
                >
                    <div className={hint ? 'mb-1' : undefined}>{label}</div>
                    {hint}
                </div>
            </div>
        );
    }

    // Use FieldGroup for OpenAPI spec select
    // Can be used with other elements when migrated to Spine
    if (
        element.element.type === 'select' &&
        'source' in element.element.options &&
        element.element.options.source === 'openapi'
    ) {
        return (
            <FieldGroup orientation="horizontal">
                <Group>
                    {label}
                    {hint}
                </Group>
                {renderElement(element.element)}
            </FieldGroup>
        );
    }

    // Horizontal, input on the right
    return (
        <div className="flex items-center">
            <div
                role="button"
                tabIndex={0}
                className="flex-1 cursor-pointer"
                onClick={onPress}
                onKeyDown={onLabelKeyDown}
            >
                <div className={hint ? 'mb-1' : undefined}>{label}</div>
                {hint}
            </div>
            <div
                className="pl-4"
                style={element.element.type === 'select' ? { flex: 0.4 } : undefined}
            >
                {element.element.type === 'select' ? (
                    <ContentKitSelect
                        {...props}
                        element={element.element}
                        /**
                         * The custom style is needed to prevent the select from overflowing.
                         * Ideally we should be able to remove this once we have spine components
                         * properly implemented to take care of this (horizontal layout, overflow, etc.)
                         */
                        style={{ width: 'min-content', maxWidth: '100%', alignSelf: 'flex-end' }}
                    />
                ) : (
                    renderElement(element.element)
                )}
            </div>
        </div>
    );
}
